import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RichTextData, RichTextMentions } from 'ui';
import type { MentionOption } from '../types';
import { sendMessage } from '../utilities';

export const useNativeBridge = () => {
  const [data, setData] = useState<string>();
  const [editorData, setEditorData] = useState<RichTextData>({ html: '', text: '' });
  const [isFocused, setIsFocused] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const mentionResolvers = useRef<Record<string, (mentions: MentionOption[]) => void>>({});

  const onChange = useCallback(
    (changedData: RichTextData) => {
      sendMessage('onChangeData', JSON.stringify({ ...changedData, uploading: isUploading }));
      setEditorData(changedData);
    },
    [isUploading],
  );

  const onUploading = useCallback(
    (uploading: boolean) => {
      sendMessage('onChangeData', JSON.stringify({ ...editorData, uploading }));
      setIsUploading(uploading);
    },
    [editorData],
  );

  const getMentions = useCallback<RichTextMentions>((value): MentionOption[] | Promise<MentionOption[]> => {
    if (!value) {
      return [];
    }

    return new Promise<MentionOption[]>((resolve) => {
      mentionResolvers.current[value] = resolve;
      sendMessage('getMentions', value);
    });
  }, []);

  useEffect(() => {
    const resolvers = mentionResolvers.current;

    window.app = {
      onMentions(value, mentions) {
        resolvers[value]?.(
          (mentions ?? []).map((user) => ({ id: `@${user.name}`, link: `/members/${user.id}`, userId: user.id })),
        );
      },
      setData,
      setFocus() {
        setIsFocused(true);
      },
    };

    sendMessage('onReady');

    return () => {
      delete window.app;

      for (const resolve of Object.values(resolvers)) {
        resolve([]);
      }

      mentionResolvers.current = {};
    };
  }, []);

  return useMemo(
    () => ({ data, getMentions, isFocused, onChange, onUploading }),
    [data, getMentions, isFocused, onChange, onUploading],
  );
};
